/**
 * KOINE CO — la cabecera se comporta como el running header de un libro.
 *
 * En las paginas de Proverbios, arriba va impreso el nombre del libro en cada
 * hoja. Aqui la barra hace lo mismo con la SECCION: segun avanzas, muestra en
 * que capitulo de la pagina estas, igual que una cabecera impresa.
 *
 * POR QUE ASI Y NO CON EFECTOS DE ENTRADA
 * ---------------------------------------
 * La tentacion es poner un fade-up escalonado en cada seccion. Eso es
 * exactamente lo que delata una pagina generada: efecto repartido, sin motivo,
 * igual en todos los bloques. Un solo gesto orquestado que ademas ORIENTA vale
 * mas que seis decorativos.
 *
 * SIN LIBRERIAS, y no por ahorrar kilobytes: la norma del tema es API nativa
 * del navegador. IntersectionObserver hace esto sin coste de scroll.
 *
 * NO SE ESCUCHA `scroll` EN NINGUN MOMENTO. Dirigir animacion desde el evento
 * de scroll obliga al navegador a recalcular en cada frame; el observer avisa
 * solo cuando algo entra o sale, que es cuando hay algo que hacer.
 */

class KoineRunningHead extends HTMLElement {
  #observer = null;
  #controller = null;
  /** Secciones visibles ahora mismo, en orden de documento. */
  #visibles = new Set();

  connectedCallback() {
    const secciones = document.querySelectorAll('[data-running-head]');
    if (secciones.length === 0) return;

    this.#controller = new AbortController();

    // rootMargin recorta la mitad superior: asi la cabecera cambia cuando la
    // seccion llega al centro de la pantalla, no en cuanto asoma un pixel por
    // abajo. Sin esto parpadearia en cada scroll pequeño.
    this.#observer = new IntersectionObserver(
      (entradas) => {
        for (const e of entradas) {
          if (e.isIntersecting) this.#visibles.add(e.target);
          else this.#visibles.delete(e.target);
        }
        this.#pintar(secciones);
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 },
    );

    for (const s of secciones) this.#observer.observe(s);

    // Escucha en la ventana: desde el 19 sep 2026 el scroll vuelve a ser del
    // documento (ver "QUIEN HACE EL SCROLL" en koine-theme.css). Antes era
    // `.page-wrapper`, y ese elemento ya no se desplaza. Es el unico listener,
    // pasivo y sin animacion detras.
    window.addEventListener('scroll', () => this.#pintar(secciones), {
      passive: true,
      signal: this.#controller.signal,
    });
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#controller?.abort();
  }

  #pintar(secciones) {
    // La primera en orden de documento de las que estan a la vista: si dos se
    // solapan, manda la de arriba, como al pasar una hoja.
    let actual = null;
    for (const s of secciones) {
      if (this.#visibles.has(s)) {
        actual = s;
        break;
      }
    }

    const texto = actual?.dataset.runningHead ?? '';
    if (texto === this.textContent.trim()) return;

    // Cambio en dos tiempos con opacidad, que es propiedad de compositor.
    // 120 ms: por debajo del limite de 200 que se fija para feedback.
    this.dataset.swapping = 'true';
    window.setTimeout(() => {
      this.textContent = texto;
      delete this.dataset.swapping;
    }, 120);
  }
}

if (!customElements.get('koine-running-head')) {
  customElements.define('koine-running-head', KoineRunningHead);
}

/* ===================================================================== */

/**
 * ALTA EN LA LISTA DE ESPERA — los DOS formularios del tema, con un solo
 * codigo: el de la ficha de un libro que aun no se vende, y el del pie.
 *
 * DONDE VAN LOS EMAILS, Y POR QUE NO A SHOPIFY
 * --------------------------------------------
 * A join.koineco.com/subscribe, que es el mismo sitio que la landing: KV mas Brevo,
 * que es quien manda la secuencia de 7 emails.
 *
 * SUBDOMINIO DESDE EL 29 SEP 2026. Antes era koineco.com/subscribe, pero
 * koineco.com pasa a ser la tienda, y ahi /subscribe es una ruta de Shopify
 * que no existe: las altas se perderian sin error visible.
 *
 * El del pie no era esto. Era un `{% form 'customer' %}`, o sea que creaba un
 * CLIENTE de Shopify con la etiqueta `newsletter` y ahi se quedaba: esa gente
 * no entraba en Brevo y **no recibia ni un email**. Dos listas de la misma
 * marca, una de ellas muerta. Unificado el 14 sep 2026.
 *
 * SIN JAVASCRIPT TAMBIEN FUNCIONA. El <form> apunta a join.koineco.com y un POST
 * normal de navegador no pasa por CORS: /subscribe responde con un 303 a
 * /thanks/. Esto de aqui solo evita que te saquen de la pagina.
 *
 * Y SI EL FETCH FALLA, NO SE DA ERROR: se deja que el formulario se envie a la
 * manera nativa. Un alta perdida es el peor resultado posible y hay una
 * segunda via que ya funciona.
 */
function montarAlta(form) {
  const nota = form.querySelector('[data-wl-msg]');
  const boton = form.querySelector('button[type="submit"]');
  if (!nota || !boton) return;

  // Procedencia y referido, de la URL a los campos ocultos.
  const params = new URLSearchParams(window.location.search);
  for (const campo of form.querySelectorAll('[data-wl-utm]')) {
    campo.value = params.get(campo.dataset.wlUtm) || '';
  }
  const ref = form.querySelector('[data-wl-ref]');
  if (ref) ref.value = (params.get('ref') || '').toUpperCase().slice(0, 6);

  const decir = (texto, estado) => {
    nota.textContent = texto;
    nota.dataset.estado = estado;
    nota.hidden = false;
  };

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    nota.hidden = true;

    const etiqueta = boton.textContent;
    boton.textContent = 'Joining';
    boton.disabled = true;

    try {
      const r = await fetch(form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          email: form.email.value.trim(),
          // Solo la ficha de un libro manda `book`. El del pie es generico.
          book: form.book ? form.book.value : '',
          website: form.website.value,
          ref: ref ? ref.value : '',
          utmSource: form.utmSource.value,
          utmMedium: form.utmMedium.value,
          utmCampaign: form.utmCampaign.value,
        }),
      });
      const d = await r.json();

      if (!r.ok || !d.ok) {
        // Un email mal escrito es cosa de quien lo teclea: se dice y se le
        // deja corregirlo, con el campo donde estaba.
        boton.textContent = etiqueta;
        boton.disabled = false;
        decir(d.error || 'That email address does not look right.', 'error');
        return;
      }

      // El campo ya no hace falta y dejarlo invita a mandarlo otra vez.
      for (const el of form.querySelectorAll('[data-wl-oculta]')) el.hidden = true;
      decir(form.dataset.wlOk || 'You are on the list.', 'ok');
    } catch {
      form.submit();
    }
  });
}

if (window.fetch) {
  for (const f of document.querySelectorAll('form[data-koine-waitlist]')) montarAlta(f);
}

/**
 * CABECERA QUE SE APARTA EN MOVIL (27 sep 2026).
 *
 * Por debajo de 60rem la cabecera fija se esconde al bajar y vuelve al subir,
 * que es cuando alguien busca el carrito o volver atras. Y mientras el editor
 * esta en pantalla no vuelve nunca: ahi arriba va el visor 3D, fijo tambien, y
 * la cabecera lo taparia.
 *
 * Solo pone o quita un atributo; la animacion es un transform en CSS. Hay
 * listener de scroll porque la direccion no la da un IntersectionObserver,
 * pero es pasivo y no pinta nada por frame.
 */
function cabeceraQueSeAparta() {
  const grupo = document.getElementById('header-group');
  if (!grupo) return;

  const movil = window.matchMedia('(max-width: 59.99rem)');
  const editor = document.getElementById('koine-editor');
  let editorALaVista = false;
  let ultimo = window.scrollY;

  const poner = (oculta) => grupo.toggleAttribute('data-oculta', oculta);

  const decidir = () => {
    const y = window.scrollY;
    const paso = y - ultimo;
    if (!movil.matches) {
      poner(false);
    } else if (editorALaVista && y > grupo.offsetHeight) {
      poner(true);
    } else if (y <= grupo.offsetHeight) {
      poner(false);
    } else if (Math.abs(paso) > 6) {
      // Umbral de 6 px: el rebote del dedo al soltar no la hace parpadear.
      poner(paso > 0);
    }
    if (Math.abs(paso) > 6) ultimo = y;
  };

  if (editor) {
    new IntersectionObserver(([e]) => {
      editorALaVista = e.isIntersecting;
      decidir();
    }).observe(editor);
  }

  window.addEventListener('scroll', decidir, { passive: true });
  movil.addEventListener('change', decidir);
}

cabeceraQueSeAparta();

/**
 * EL ANCLA DEL EDITOR AGUANTA EL fbclid (2 oct 2026).
 *
 * Los anuncios llevan a `/products/<libro>?utm_...#koine-editor` (sin diseño
 * ni nombre: el nombre no va nunca en una URL, ver permalink.ts). Si el
 * navegador de Facebook o Instagram pega su `fbclid` detras del ancla
 * (`#koine-editor&fbclid=...`), el id ya no coincide y la visita aterriza en
 * lo alto de la ficha, a 1.000 px del editor. Medido en iPhone: con el ancla
 * limpia salta; con el fbclid detras, no.
 */
function anclaDelEditor() {
  const h = window.location.hash;
  if (h === '#koine-editor' || !h.startsWith('#koine-editor')) return;
  document.getElementById('koine-editor')?.scrollIntoView();
}

anclaDelEditor();

/**
 * ETIQUETAS PARA MICROSOFT CLARITY (21 oct 2026, Fase 4 de ads).
 *
 * Clarity reconstruye la pagina y no ve el canvas del editor, asi que en las
 * grabaciones no se sabe en que punto esta el cliente. Esto marca momentos
 * concretos para poder filtrar las grabaciones por ellos. Nunca lleva datos
 * del cliente. Si Clarity no carga (EEE sin consentimiento), la cola se queda
 * en memoria sin hacer nada.
 *
 *   pagina-recargada   la pagina se ha vuelto a cargar (el navegador de
 *                      Instagram en iPhone parece recargarla al elegir color)
 *   entra-en-editor /  en una ficha: si aterriza en el editor (ancla) o
 *   entra-arriba       arriba, donde se ve el precio
 *   pulso-checkout     pulsa "Check out" en el lateral o en /cart
 */
function marcarClarity(evento) {
  try {
    window.clarity = window.clarity || function () { (window.clarity.q = window.clarity.q || []).push(arguments); };
    window.clarity('event', evento);
  } catch (e) { /* medir nunca rompe la tienda */ }
}

function etiquetasClarity() {
  const nav = performance.getEntriesByType?.('navigation')?.[0];
  if (nav && nav.type === 'reload') marcarClarity('pagina-recargada');

  if (window.location.pathname.includes('/products/')) {
    marcarClarity(window.location.hash.startsWith('#koine-editor') ? 'entra-en-editor' : 'entra-arriba');
  }

  // Captura y no burbuja: el lateral llega por innerHTML y el formulario de
  // /cart retiene el envio medio segundo; asi se marca antes de irse.
  document.addEventListener('click', (ev) => {
    if (ev.target.closest?.('button[name="checkout"], a[href*="/checkout"]')) marcarClarity('pulso-checkout');
  }, true);
}

etiquetasClarity();
